/**
 * Plain-text flattening for stored Slate bodies (`JSON.stringify(Descendant[])`),
 * used by the API for validation, email notification bodies, and list previews.
 *
 * Mirrors Slate's `Node.string()` by recursing the whole subtree, so text nested
 * inside `list-item` and inline `link` elements is included — a shallow
 * `children[].text` walk reports bullet-only and link-only bodies as empty even
 * though they render fine. Output matches `slateToPlainText` in
 * `@common-web/lib/slate-serialize` (top-level nodes joined by newlines), which
 * is what the editor's own emptiness check uses; keeping the two in agreement is
 * what prevents the client from submitting a body the server then rejects.
 *
 * Lives here rather than in the API so it can be shared without depending on
 * `slate`, which is a web-only dependency.
 */

type SlateNodeLike = {
  type?: string;
  text?: string;
  userId?: string;
  userName?: string;
  children?: unknown[];
};

/**
 * Flatten a single Slate node (and its subtree) to plain text. Exported so the
 * web-side `slateToPlainText` (`@common-web/lib/slate-serialize`) shares this
 * exact logic — the two emptiness checks must agree or the client submits
 * bodies the server rejects.
 */
export function slateNodeToText(node: unknown): string {
  // Malformed entries (null, primitives) must flatten to '' rather than throw —
  // a throw here would hit slateBodyToText's catch and return the raw JSON as text.
  if (node === null || typeof node !== 'object') return '';
  const {type, text, userName, children} = node as SlateNodeLike;
  // Mention elements are inline voids (empty text child); their display text
  // lives on the node itself.
  if (type === 'mention' && typeof userName === 'string') return `@${userName}`;
  if (typeof text === 'string') return text;
  if (Array.isArray(children)) return children.map(slateNodeToText).join('');
  return '';
}

/**
 * Flatten a JSON-encoded Slate body to plain text. Bodies that predate the Slate
 * migration are stored as raw strings and are returned unchanged.
 */
export function slateBodyToText(body: string): string {
  try {
    const nodes: unknown = JSON.parse(body);
    if (!Array.isArray(nodes)) return body;
    return nodes.map(slateNodeToText).join('\n').trim();
  } catch {
    return body;
  }
}

/**
 * Collect the userIds of every `mention` element in a JSON-encoded Slate body,
 * deduplicated in document order. Non-Slate (legacy plain-text) bodies contain
 * no mention nodes and return [].
 */
export function extractMentionedUserIds(body: string): string[] {
  const ids = new Set<string>();

  function walk(node: unknown): void {
    if (node === null || typeof node !== 'object') return;
    const {type, userId, children} = node as SlateNodeLike;
    if (type === 'mention' && typeof userId === 'string' && userId.length > 0) {
      ids.add(userId);
    }
    if (Array.isArray(children)) children.forEach(walk);
  }

  try {
    const nodes: unknown = JSON.parse(body);
    if (!Array.isArray(nodes)) return [];
    nodes.forEach(walk);
  } catch {
    return [];
  }
  return [...ids];
}
